import { useT } from '../lib/i18n';

export type Range = { start: string; end: string };
export type WeekHours = Partial<Record<'1' | '2' | '3' | '4' | '5' | '6' | '7', Range[]>>;
const days = ['1', '2', '3', '4', '5', '6', '7'] as const;

/** Opening hours or own working hours: per day open/closed and one or more time blocks. */
export function WeekHoursEditor({ value, onChange, closedLabel }: { value: WeekHours; onChange: (v: WeekHours) => void; closedLabel: string }) {
  const t = useT();
  const set = (day: (typeof days)[number], ranges: Range[]) => onChange({ ...value, [day]: ranges });

  function copyToOpenDays(from: (typeof days)[number]) {
    const source = value[from] ?? [];
    const next: WeekHours = { ...value };
    for (const d of days) if ((next[d]?.length ?? 0) > 0) next[d] = source.map((r) => ({ ...r }));
    onChange(next);
  }

  return (
    <div className="flex flex-col divide-y divide-line">
      {days.map((day) => {
        const ranges = value[day] ?? [];
        const open = ranges.length > 0;
        return (
          <div key={day} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
            <div className="flex w-full items-center justify-between sm:w-44 sm:pt-2">
              <span className="font-medium">{t(`day.${day}`)}</span>
              <button
                type="button"
                role="switch"
                aria-checked={open}
                aria-label={`${t(`day.${day}`)}: ${open ? t('hours.open') : closedLabel}`}
                onClick={() => set(day, open ? [] : [{ start: '09:00', end: '17:30' }])}
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors sm:hidden ${open ? 'bg-accent' : 'bg-switch-off'}`}
              >
                <span className={`absolute left-0 top-[2px] size-6 rounded-full bg-surface shadow transition-transform ${open ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
              </button>
            </div>
            <div className="flex flex-1 flex-col gap-2">
              {!open && (
                <div className="flex items-center gap-3 sm:min-h-11">
                  <span className="text-muted">{closedLabel}</span>
                  <button type="button" className="hidden text-sm font-semibold text-accent-strong hover:underline sm:inline" onClick={() => set(day, [{ start: '09:00', end: '17:30' }])}>
                    + {t('hours.open')}
                  </button>
                </div>
              )}
              {ranges.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="time"
                    step={300}
                    aria-label={`${t(`day.${day}`)} ${t('block.from')}`}
                    value={r.start}
                    onChange={(e) => set(day, ranges.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))}
                    className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-2 tabular-nums"
                  />
                  <span className="text-muted">–</span>
                  <input
                    type="time"
                    step={300}
                    aria-label={`${t(`day.${day}`)} ${t('block.until')}`}
                    value={r.end}
                    onChange={(e) => set(day, ranges.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))}
                    className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-2 tabular-nums"
                  />
                  <button
                    type="button"
                    aria-label={t('hours.removeBlock')}
                    onClick={() => set(day, ranges.filter((_, j) => j !== i))}
                    className="min-h-11 rounded-[var(--radius-control)] px-3 text-muted hover:bg-subtle"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {open && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {ranges.length < 4 && (
                    <button
                      type="button"
                      className="text-sm font-semibold text-accent-strong hover:underline"
                      onClick={() => {
                        const last = ranges[ranges.length - 1]!;
                        set(day, [...ranges, { start: last.end, end: last.end < '20:00' ? '21:00' : '23:00' }]);
                      }}
                    >
                      + {t('hours.addBlock')}
                    </button>
                  )}
                  <button type="button" className="text-sm text-muted hover:underline" onClick={() => copyToOpenDays(day)}>
                    {t('hours.copyToWeekdays')}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
